import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  DatePicker,
  TimePicker,
  InputNumber,
  Modal,
  Select,
  Radio,
  Checkbox,
  Divider,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ReactSVG } from 'react-svg';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import PhoneInput from 'react-phone-input-2';

import './Order.scss';
import { DATE_BACKEND_FORMAT, DAY_FORMAT, TIME_FORMAT, ORDER_TYPES } from '../../constants';
import AddressInput from '../Inputs/AddressInput';
import { useDispatch, useSelector } from 'react-redux';
import { doGetHubs } from '../../modules/hubs/stores/thunk';
import { selectAllHubs } from '../../modules/hubs/stores/slice';
import { doGetPartners } from '../../modules/partners/stores/thunk';
import { selectAllPartners } from '../../modules/partners/stores/slice';
import { getPartnerItemTypes } from '../../modules/partners/api';
import TagInput from '../Elements/TagInput';

const initStateModal = {
  open: false,
};

const OrderForm = ({ isFetching, handleSubmit }) => {
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [countryPickupPhone, setCountryPickupPhone] = useState();
  const [countryDeliveryPhone, setCountryDeliveryPhone] = useState();
  const [types, setTypes] = useState([]);
  const [ticketNames, setTicketNames] = useState([]);
  const [stateModal, setStateModal] = useState(initStateModal);
  const [orderType, setOrderType] = useState(ORDER_TYPES.STANDARD);
  const [productTypes, setProductTypes] = useState([]);

  const hubs = useSelector(selectAllHubs);
  const partners = useSelector(selectAllPartners);

  const selectedPartnerId = Form.useWatch('partnerId', form);
  const productDetailsKnown = Form.useWatch('productDetailsKnown', form);

  // Fetch PRODUCT item types when partner changes
  useEffect(() => {
    if (!selectedPartnerId) {
      setProductTypes([]);
      return;
    }
    getPartnerItemTypes(selectedPartnerId).then(({ response }) => {
      const items = Array.isArray(response) ? response : [];
      setProductTypes(items.filter((t) => t.category === 'PRODUCT' && t.isActive));
    });
  }, [selectedPartnerId]);

  const handleOnFinish = async (values) => {
    const params = setParams(values);
    handleSubmit && handleSubmit(params);
  };

  const handleOnFinishSelectedHub = (values) => {
    const hub = hubs.find((hub) => hub.id == values.hubId);
    if (hub) {
      form.setFieldsValue({
        pickupAddress: {
          address: hub.startAddress,
          latLng: {
            lat: hub.startAddressLat,
            lng: hub.startAddressLng,
          },
        },
        pickupName: hub.name,
      });
    }
    setStateModal({
      ...stateModal,
      open: false,
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(doGetHubs({ take: 1000000 }));
    dispatch(doGetPartners({ take: 1000000 }));
  }, []);
  const setParams = (values) => {
    const params = { ...values };
    params.orderType = orderType;
    params.pickupPhone =
      !isEmpty(values.pickupPhone) && countryPickupPhone?.dialCode !== values.pickupPhone
        ? `${values.pickupPhone?.includes('+') ? '' : '+'}${values.pickupPhone}`
        : null;
    params.pickupAddress = values.pickupAddress?.address;
    params.pickupAddressLat = values.pickupAddress?.latLng?.lat;
    params.pickupAddressLng = values.pickupAddress?.latLng?.lng;
    params.pickupTimeStart = values.pickupTimeStart ? values.pickupTimeStart.format(TIME_FORMAT) : null;
    params.pickupTimeEnd = values.pickupTimeEnd ? values.pickupTimeEnd.format(TIME_FORMAT) : null;
    params.pickupDuration = values.pickupDuration ? Number(values.pickupDuration) : null;
    params.pickupEmail = values.pickupEmail || null;

    // Delivery fields only for standard orders
    if (orderType !== ORDER_TYPES.SERVICE) {
      params.deliveryPhone =
        !isEmpty(values.deliveryPhone) && countryDeliveryPhone?.dialCode !== values.deliveryPhone
          ? `${values.deliveryPhone?.includes('+') ? '' : '+'}${values.deliveryPhone}`
          : null;
      params.deliveryEmail = values.deliveryEmail || null;
      params.deliveryAddress = values.deliveryAddress?.address;
      params.deliveryAddressLat = values.deliveryAddress?.latLng?.lat;
      params.deliveryAddressLng = values.deliveryAddress?.latLng?.lng;
      params.deliveryTimeStart = values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT) : null;
      params.deliveryTimeEnd = values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT) : null;
      params.deliveryDuration = values.deliveryDuration ? Number(values.deliveryDuration) : null;
    } else {
      // Clear delivery fields for SERVICE
      params.deliveryPhone = null;
      params.deliveryEmail = null;
      params.deliveryAddress = null;
      params.deliveryAddressLat = null;
      params.deliveryAddressLng = null;
      params.deliveryTimeStart = null;
      params.deliveryTimeEnd = null;
      params.deliveryDuration = null;
      params.deliveryName = null;
    }

    params.deliveryDate = values.deliveryDate ? values.deliveryDate.format(DATE_BACKEND_FORMAT) : null;
    params.sizeLength = values.sizeLength ? Number(values.sizeLength) : null;
    params.sizeWidth = values.sizeWidth ? Number(values.sizeWidth) : null;
    params.sizeHeight = values.sizeHeight ? Number(values.sizeHeight) : null;
    params.weight = values.weight ? Number(values.weight) : null;
    params.types = types;
    params.ticketNames = ticketNames;

    // Partner: send partnerId from the select, and also set partner string for backward compat
    if (values.partnerId) {
      const selectedPartner = partners.find((p) => p.id === values.partnerId);
      params.partnerId = values.partnerId;
      params.partner = selectedPartner?.name || null;
    } else {
      params.partnerId = null;
      params.partner = null;
    }

    params.partnerContact = values.partnerContact || null;
    params.partnerOrderRef = values.partnerOrderRef || null;
    params.tourRef = values.tourRef || null;
    params.airtableOrderRecordRef = values.airtableOrderRecordRef || null;
    params.preparationMode = values.preparationMode || null;
    params.collectionForm = values.collectionForm || null;

    // Product details
    if (values.partnerId && productTypes.length > 0) {
      params.productDetailsKnown = values.productDetailsKnown ?? true;
      if (values.productDetailsKnown && values.orderProducts?.length > 0) {
        params.orderProducts = values.orderProducts.map((p) => ({
          partnerItemTypeId: p.partnerItemTypeId,
          quantity: p.quantity,
          notes: p.notes || null,
        }));
      } else {
        params.orderProducts = [];
      }
    }

    return params;
  };

  const handleOnChangePickupFrom = (value) => {
    form.setFieldsValue({ pickupTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('pickupTimeEnd'))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue('pickupTimeEnd') < value) {
      form.setFields([
        {
          name: 'pickupTimeStart',
          errors: ['Shift End must be less than Shift Start'],
        },
      ]);
    }
  };

  const handleOnChangePickupTo = (value) => {
    form.setFieldsValue({ pickupTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('pickupTimeStart'))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue('pickupTimeStart') > value) {
      form.setFields([
        {
          name: 'pickupTimeEnd',
          errors: ['Pickup To must be greater than Pickup From'],
        },
      ]);
    }
  };

  const resetErrorPickupTime = () => {
    form.setFields([
      {
        name: 'pickupTimeStart',
        errors: [],
      },
      {
        name: 'pickupTimeEnd',
        errors: [],
      },
    ]);
  };

  const handleOnChangeDeliveryFrom = (value) => {
    form.setFieldsValue({ deliveryTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('deliveryTimeEnd'))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue('deliveryTimeEnd') < value) {
      form.setFields([
        {
          name: 'deliveryTimeStart',
          errors: ['Delivery From must be less than Delivery To'],
        },
      ]);
    }
  };

  const handleOnChangeDeliveryTo = (value) => {
    form.setFieldsValue({ deliveryTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('deliveryTimeStart'))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue('deliveryTimeStart') > value) {
      form.setFields([
        {
          name: 'deliveryTimeEnd',
          errors: ['Delivery To must be greater than Delivery From'],
        },
      ]);
    }
  };

  const resetErrorDeliveryTime = () => {
    form.setFields([
      {
        name: 'deliveryTimeStart',
        errors: [],
      },
      {
        name: 'deliveryTimeEnd',
        errors: [],
      },
    ]);
  };

  const onAddFromHubs = () => {
    form2.resetFields();
    setStateModal({
      ...stateModal,
      open: true,
    });
  };

  return (
    <div className="OrderForm">
      <Spin spinning={isFetching}>
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          initialValues={{
            pickupAddress: {},
            deliveryAddress: {},
            pickupPhone: '+41',
            deliveryPhone: '+41',
            orderType: ORDER_TYPES.STANDARD,
            productDetailsKnown: true,
          }}
        >
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">Order Type</div>
              </div>
              <div className="SectionContent">
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item label="Type" name="orderType">
                      <Radio.Group
                        value={orderType}
                        onChange={(e) => {
                          setOrderType(e.target.value);
                          form.setFieldsValue({ orderType: e.target.value });
                          // Clear delivery field errors when switching to SERVICE
                          if (e.target.value === ORDER_TYPES.SERVICE) {
                            form.setFields([
                              { name: 'deliveryName', errors: [] },
                              { name: 'deliveryAddress', errors: [] },
                            ]);
                          }
                        }}
                      >
                        <Radio.Button value={ORDER_TYPES.STANDARD}>Standard Delivery</Radio.Button>
                        <Radio.Button value={ORDER_TYPES.SERVICE}>Service</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">Pickup</div>
                <div className="Right">
                  <Button type="link" ghost onClick={() => onAddFromHubs()}>
                    Add from Hubs
                    <ReactSVG className="icon" src="/icons/all_inbox.svg" />
                  </Button>
                </div>
              </div>
              <div className="SectionContent">
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item label="Pickup Name" name="pickupName" rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="Pickup Name" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Pickup Phone Number" name="pickupPhone">
                      <PhoneInput
                        country={'ch'}
                        size="normal"
                        prefix="+"
                        specialLabel=""
                        countryCodeEditable
                        enableSearch
                        disableSearchIcon
                        onChange={(value, country) => {
                          setCountryPickupPhone(country);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Pickup From" name="pickupTimeStart">
                      <TimePicker
                        placeholder="Pickup From"
                        format={TIME_FORMAT}
                        minuteStep={30}
                        style={{ width: '100%' }}
                        onChange={handleOnChangePickupFrom}
                        onSelect={handleOnChangePickupFrom}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Pickup To" name="pickupTimeEnd">
                      <TimePicker
                        placeholder="Pickup To"
                        format={TIME_FORMAT}
                        minuteStep={30}
                        style={{ width: '100%' }}
                        onChange={handleOnChangePickupTo}
                        onSelect={handleOnChangePickupTo}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Pickup Email"
                      name="pickupEmail"
                      rules={[{ type: 'email', message: 'Invalid Email' }]}
                    >
                      <Input placeholder=" Pickup Email" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Pickup Address"
                      name="pickupAddress"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                              return Promise.resolve();
                            }

                            if (isEmpty(value?.address)) {
                              return Promise.reject(new Error('Required'));
                            }

                            return Promise.reject(new Error('Invalid'));
                          },
                        },
                      ]}
                    >
                      <AddressInput
                        placeholder="Pickup Address"
                        onSelect={(value) => form.setFieldsValue({ pickupAddress: value })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Pickup Duration" name="pickupDuration">
                      <Input placeholder="Pickup Duration" suffix="minutes" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          {orderType !== ORDER_TYPES.SERVICE && (
            <div className="FormContent">
              <div className="Section">
                <div className="SectionHeader">
                  <div className="Left">Delivery</div>
                </div>
                <div className="SectionContent">
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item
                        label="Delivery Name"
                        name="deliveryName"
                        rules={[{ required: orderType !== ORDER_TYPES.SERVICE, message: 'Required' }]}
                      >
                        <Input placeholder="Delivery Name" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Delivery Phone Number" name="deliveryPhone">
                        <PhoneInput
                          country={'ch'}
                          prefix="+"
                          specialLabel=""
                          enableSearch
                          disableSearchIcon
                          onChange={(value, country) => {
                            setCountryDeliveryPhone(country);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Delivery From" name="deliveryTimeStart">
                        <TimePicker
                          placeholder="Delivery From"
                          format={TIME_FORMAT}
                          minuteStep={30}
                          style={{ width: '100%' }}
                          onChange={handleOnChangeDeliveryFrom}
                          onSelect={handleOnChangeDeliveryFrom}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Delivery To" name="deliveryTimeEnd">
                        <TimePicker
                          placeholder="Delivery To"
                          format={TIME_FORMAT}
                          minuteStep={30}
                          style={{ width: '100%' }}
                          onChange={handleOnChangeDeliveryTo}
                          onSelect={handleOnChangeDeliveryTo}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="Delivery Email"
                        name="deliveryEmail"
                        rules={[{ type: 'email', message: 'Invalid Email' }]}
                      >
                        <Input placeholder="Delivery Email" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Delivery Address"
                        name="deliveryAddress"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (orderType === ORDER_TYPES.SERVICE) {
                                return Promise.resolve();
                              }
                              if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                                return Promise.resolve();
                              }

                              if (isEmpty(value?.address)) {
                                return Promise.reject(new Error('Required'));
                              }

                              return Promise.reject(new Error('Invalid'));
                            },
                          },
                        ]}
                      >
                        <AddressInput
                          placeholder="Delivery Address"
                          onSelect={(value) => form.setFieldsValue({ deliveryAddress: value })}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Delivery Duration" name="deliveryDuration">
                        <Input placeholder="Delivery Duration" suffix="minutes" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          )}
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">Order Info</div>
              </div>
              <div className="SectionContent">
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="Name" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Order Ref" name="orderRef">
                      <Input placeholder="Order Ref" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Delivery Date"
                      name="deliveryDate"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <DatePicker placeholder="Delivery Date" format={DAY_FORMAT} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Volume" name="volume">
                      <InputNumber placeholder="Volume" step={0.001} style={{ width: '100%' }} suffix="m³" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item name="weight" label="Weight">
                      <Input min={1} type="number" suffix="kg" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="sizeLength" label="Size (LxWxH)">
                      <Input min={1} type="number" suffix="cm" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="sizeWidth" label=" ">
                      <Input min={1} type="number" suffix="cm" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="sizeHeight" label=" ">
                      <Input min={1} type="number" suffix="cm" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Description" name="description">
                      <Input.TextArea rows={4} placeholder="Description" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Pickup Note" name="pickupNote">
                      <Input.TextArea rows={4} placeholder="Pickup Note" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Delivery Note" name="deliveryNote">
                      <Input.TextArea rows={4} placeholder="Delivery Note" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Type" name="types">
                      <TagInput size="normal" tags={types} onChange={(values) => setTypes(values)} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Tickets" name="ticketNames">
                      <TagInput size="normal" tags={ticketNames} onChange={(values) => setTicketNames(values)} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Partner" name="partnerId">
                      <Select
                        placeholder="Select a partner"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={partners.map((p) => ({
                          label: p.name,
                          value: p.id,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Order Products Section — only when partner has PRODUCT item types */}
                {selectedPartnerId && productTypes.length > 0 && (
                  <>
                    <Divider orientation="left">Products</Divider>
                    <Row gutter={24}>
                      <Col span={24}>
                        <Form.Item name="productDetailsKnown" valuePropName="checked" style={{ marginBottom: 12 }}>
                          <Checkbox>Product details known at order creation</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                    {productDetailsKnown && (
                      <Form.List name="orderProducts">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                                <Col span={10}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'partnerItemTypeId']}
                                    rules={[{ required: true, message: 'Select product' }]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select
                                      placeholder="Select product"
                                      options={productTypes.map((t) => ({
                                        label: `${t.name}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
                                        value: t.id,
                                      }))}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'quantity']}
                                    rules={[{ required: true, message: 'Qty' }]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber min={0.01} step={0.1} placeholder="Qty" style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, 'notes']} style={{ marginBottom: 0 }}>
                                    <Input placeholder="Notes (optional)" />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() => add({ quantity: 1 })}
                              icon={<PlusOutlined />}
                              style={{ width: '50%' }}
                            >
                              Add Product
                            </Button>
                          </>
                        )}
                      </Form.List>
                    )}
                    <Divider />
                  </>
                )}

                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item label="Partner Contact" name="partnerContact">
                      <Input placeholder="Partner Contact" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Partner Order Ref" name="partnerOrderRef">
                      <Input placeholder="Partner Order Ref" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Tour Ref" name="tourRef">
                      <Input placeholder="Tour Ref" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Airtable Order Record Ref" name="airtableOrderRecordRef">
                      <Input placeholder="Airtable Order Record Ref" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Preparation Mode" name="preparationMode">
                      <Input placeholder="Preparation Mode" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Collection Form" name="collectionForm">
                      <Input placeholder="Collection Form" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Link to={'/orders'}>
                <Button key="cancel">Cancel</Button>
              </Link>
              <Button htmlType="submit" type="primary">
                Create
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
      <Modal
        open={stateModal.open}
        onCancel={() => setStateModal({ ...stateModal, open: false })}
        onOk={() => form2.submit()}
        width={792}
        forceRender
      >
        <Form
          form={form2}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinishSelectedHub}
          initialValues={{
            hubId: undefined,
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item label="Hub" name="hubId" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  size="large"
                  options={hubs.map((hub) => ({
                    label: hub.name,
                    value: hub.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderForm;
